import { useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';

import { calendarDateLabel } from '../../timesheets/calendarDates.js';
import { useTimesheetsQuery } from '../../timesheets/useTimesheetsQuery.js';

import { HOME_CARD_LINK, HOME_CARD_ROW, HomeCard, homeCardStatus } from './HomeCard.js';

/** Five requests before the card says how many more are waiting. */
const MAX_ROWS = 5;

/**
 * "Time to approve" — the edit requests a supervisor has not answered yet.
 *
 * NO NEW ENDPOINT, and not `GET /api/v1/timesheets/edit-requests` either: the week-grid response
 * this screen already fetches carries the same `pendingRequests` queue, tenant-wide for anybody who
 * is not the Employee role. One request feeds this card and the on-clock card beside it.
 *
 * The card shows WHAT is being asked, not a bare count. A count sends somebody to another screen to
 * find out whether it is worth going; the employee, the job and the hours asked for are enough to
 * decide from here. Approving still happens on Timesheets, where the diff and the reason are shown
 * in full and the server re-checks `time.approve`.
 */
export function DispatcherApprovals() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const query = useTimesheetsQuery({});

  const requests = query.data?.pendingRequests ?? [];
  const shown = requests.slice(0, MAX_ROWS);

  return (
    <HomeCard
      headingId="dispatcher-approvals-heading"
      title={t('home:dispatcher.approvals.heading')}
      action={
        <Link to="/timesheets" search={{}} className={HOME_CARD_LINK}>
          {t('home:dispatcher.approvals.review')}
        </Link>
      }
      status={homeCardStatus(query, shown.length)}
      errorText={t('home:dispatcher.approvals.unavailable')}
      emptyText={t('home:dispatcher.approvals.empty')}
      onRetry={() => void query.refetch()}
    >
      <ul className="mt-3 flex flex-col gap-2">
        {shown.map((request) => (
          <li key={request.id} className={HOME_CARD_ROW}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <span className="font-medium text-foreground text-sm">{request.employeeName}</span>
              <span className="font-mono text-muted-foreground text-xs tabular-nums">
                {t('home:dispatcher.approvals.hoursChange', {
                  from: request.original.hours,
                  to: request.proposed.hours,
                })}
              </span>
            </div>
            <span className="text-muted-foreground text-xs">
              {request.jobName} · {calendarDateLabel(locale, request.original.date)}
            </span>
          </li>
        ))}
        {requests.length > shown.length ? (
          <li className="text-muted-foreground text-xs">
            {t('home:dispatcher.approvals.more', { count: requests.length - shown.length })}
          </li>
        ) : null}
      </ul>
    </HomeCard>
  );
}
